import { BARE_HAND_MULT, BUILDINGS, ITEMS, TOOL_GATHER_MULT } from '../../shared/constants'
import { farmSkillMult, isArmed, jobSkill, personTitle, requiredTool, skillMult, SKILL_LABEL } from '../../shared/person'
import type { Building, BuildingType, UnitWire } from '../../shared/types'
import { BUILD_ICON, colorOf, el, unitActivity, type UICtx } from './panels'
import { itemIconSvg } from './itemIcons'
import { crewPortrait } from './crewPortrait'

type Filter = 'all' | 'available' | 'skilled'
type Candidate = { unit: UnitWire; post?: Building; activity: string; unavailable: string; available: boolean; equipped: boolean; skill: number }

/** A persistent crew editor. Commands use the normal server-owned crew rules;
 * opening/closing and filtering never change jobs or inventory. */
export function initAssignModal({ st, tx, root }: UICtx, previewFor: (type: BuildingType) => string | undefined): { open: (id: number) => void; refresh: () => void } {
  const back = el('div', 'modal-back crew-back hidden')
  const panel = el('div', 'panel modal crew-dialog')
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'crew-dialog-title')
  panel.setAttribute('aria-describedby', 'crew-dialog-subtitle')
  const head = el('div', 'crew-dialog-head')
  const art = el('div', 'crew-building-art')
  art.setAttribute('aria-hidden', 'true')
  const heading = el('div', 'crew-heading')
  const eyebrow = el('div', 'crew-eyebrow', 'YOUR PEOPLE · YOUR PLAN')
  const title = el('h2', 'modal-title', 'Choose your crew')
  title.id = 'crew-dialog-title'
  const subtitle = el('p', 'crew-subtitle')
  subtitle.id = 'crew-dialog-subtitle'
  heading.append(eyebrow, title, subtitle)
  const close = el('button', 'btn btn-ghost crew-close', '✕')
  close.type = 'button'; close.setAttribute('aria-label', 'Close crew menu')
  head.append(art, heading, close)

  const scroll = el('div', 'crew-scroll')
  const overview = el('section', 'crew-overview')
  const overviewHead = el('div', 'crew-section-head')
  const count = el('span', 'crew-count')
  overviewHead.append(el('h3', '', 'Your crew'), count)
  const slots = el('div', 'crew-slots')
  overview.append(overviewHead, slots)
  const toolbar = el('div', 'crew-toolbar')
  const searchLabel = el('label', 'crew-search')
  const searchIcon = el('span', '', '⌕'); searchIcon.setAttribute('aria-hidden', 'true')
  const search = el('input', '')
  search.type = 'search'; search.placeholder = 'Find a villager…'
  search.setAttribute('aria-label', 'Search villagers by name, equipment or job')
  search.autocomplete = 'off'
  searchLabel.append(searchIcon, search)
  const filters = el('div', 'crew-filters')
  filters.setAttribute('role', 'group'); filters.setAttribute('aria-label', 'Find crew members')
  for (const [key, label] of [['all', 'All villagers'], ['available', 'Available'], ['skilled', 'Highest skill']] as const) {
    const button = el('button', '', label)
    button.type = 'button'; button.dataset.crewFilter = key
    filters.append(button)
  }
  toolbar.append(searchLabel, filters)
  const listHead = el('div', 'crew-list-head')
  const listTitle = el('h3', '', 'Choose your people')
  const results = el('span', 'crew-results')
  listHead.append(listTitle, results)
  const candidates = el('div', 'crew-candidates')
  scroll.append(overview, toolbar, listHead, candidates)

  const foot = el('div', 'crew-dialog-foot')
  const feedback = el('div', 'crew-feedback')
  feedback.setAttribute('role', 'status'); feedback.setAttribute('aria-live', 'polite')
  const actions = el('div', 'crew-footer-actions')
  const auto = el('button', 'btn btn-ghost crew-auto', 'Use automatic staffing')
  auto.type = 'button'
  const done = el('button', 'btn crew-done', 'Done'); done.type = 'button'
  actions.append(auto, done); foot.append(feedback, actions)
  panel.append(head, scroll, foot); back.append(panel); root.append(back)

  let buildingId: number | null = null
  let previousFocus: HTMLElement | null = null
  let filter: Filter = 'all'
  let signature = ''
  let gesture = false
  let pending: { unit: number; add: boolean; name: string; at: number } | null = null
  let notice = ''

  const current = (): Building | undefined => {
    const b = buildingId === null ? undefined : st.buildings.get(buildingId)
    return b && b.owner === st.me && b.done && (BUILDINGS[b.type].workerSlots ?? 0) > 0 ? b : undefined
  }

  function hide(): void {
    buildingId = null; pending = null; gesture = false
    back.classList.add('hidden')
    // The selection panel refreshes during play, so the opener may have been
    // replaced. Return to its current equivalent without moving the camera.
    const target = previousFocus?.isConnected ? previousFocus : root.querySelector<HTMLElement>('[data-building-action="manage-crew"]')
    target?.focus({ preventScroll: true })
  }
  close.onclick = hide; done.onclick = hide
  back.addEventListener('click', e => { if (e.target === back) hide() })
  auto.onclick = () => {
    const b = current()
    if (!b || !st.connected || pending || st.self?.governor?.enabled === false) return
    tx({ t: 'staffing', id: b.id, automatic: true }); hide()
  }

  // Keep game hotkeys outside this dialog, including while a button has focus.
  window.addEventListener('keydown', e => {
    if (buildingId === null || back.inert) return
    e.stopImmediatePropagation()
    if (e.key === 'Escape') { e.preventDefault(); hide(); return }
    if ((e.key === ' ' || e.key === 'Enter') && document.activeElement instanceof HTMLButtonElement) gesture = true
    if (e.key !== 'Tab') return
    const controls = [...panel.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)')]
      .filter(node => node.getClientRects().length > 0)
    const first = controls[0], last = controls[controls.length - 1]
    if (e.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
      e.preventDefault(); last?.focus()
    } else if (!e.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
      e.preventDefault(); first?.focus()
    }
  }, true)
  // Key releases must still reach the map's held-key cleanup.
  window.addEventListener('keyup', e => { if (e.key === ' ' || e.key === 'Enter') release() })

  // Native click handles touch/keyboard activation and cancels a scrolling
  // swipe. A live update must not replace that button between down and click.
  panel.addEventListener('pointerdown', () => { gesture = true }, { passive: true })
  const release = (): void => {
    if (!gesture) return
    requestAnimationFrame(() => { gesture = false; refresh() })
  }
  window.addEventListener('pointerup', release)
  window.addEventListener('pointercancel', release)
  window.addEventListener('blur', release)
  search.oninput = () => { signature = ''; refresh() }
  filters.onclick = e => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-crew-filter]')
    if (!button) return
    filter = button.dataset.crewFilter as Filter
    signature = ''; refresh()
  }

  function rowsFor(b: Building): Candidate[] {
    const posts = new Map<number, Building>()
    for (const other of st.buildings.values()) if (other.owner === st.me) {
      for (const id of other.crew ?? []) posts.set(id, other)
    }
    const away = new Set(st.self?.realm?.expedition?.party ?? [])
    const skill = jobSkill(b.type)
    return [...st.units.values()].filter(u => u.owner === st.me && u.type === 'citizen' && u.person && u.hp > 0)
      .map(unit => {
        const post = posts.get(unit.id)
        const unavailable = away.has(unit.id) ? 'Away on an expedition' : unit.hauler ? 'On hauling duty' : isArmed(unit.person!) ? 'Stow their weapon before assigning work' : ''
        const activity = post ? `${post.id === b.id ? 'Assigned here' : `Working at ${post.name || BUILDINGS[post.type].name}`} · ${unitActivity(st, unit)}` : unitActivity(st, unit)
        const available = !unavailable && !post && unit.job === undefined && !unit.exploring && !unit.errand && unit.supply === undefined && unit.state === 'idle' && (unit.manualUntil ?? 0) <= st.tick
        return { unit, post, activity, unavailable, available, equipped: unit.person!.held === requiredTool(b.type), skill: unit.person!.skill[skill] ?? 0 }
      })
  }

  function sendCrew(unitId: number, add: boolean): void {
    const b = current()
    if (!b || !st.connected || pending) return
    const row = rowsFor(b).find(r => r.unit.id === unitId)
    const crew = b.crew ?? []
    if (add && (!row || row.unavailable)) return
    if (!add && row?.unit.hauler) return
    if (add && (crew.includes(unitId) || crew.length >= (BUILDINGS[b.type].workerSlots ?? 0))) return
    if (!add && !crew.includes(unitId)) return
    pending = { unit: unitId, add, name: row?.unit.person?.name ?? `Villager #${unitId}`, at: Date.now() }
    notice = ''
    tx({ t: 'crew', id: b.id, unit: unitId, add })
    signature = ''; refresh()
  }
  panel.addEventListener('click', e => {
    const button = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-crew-add], [data-crew-remove]')
    if (!button || button.disabled) return
    const add = button.dataset.crewAdd !== undefined
    sendCrew(Number(add ? button.dataset.crewAdd : button.dataset.crewRemove), add)
  })
  st.on('error', message => {
    if (buildingId === null || !pending) return
    pending = null; notice = typeof message === 'string' ? message : 'Could not update this crew.'
    signature = ''; refresh()
  })

  function refresh(): void {
    if (buildingId === null || gesture) return
    const b = current()
    if (!b || st.self?.ruined) { hide(); return }
    const crew = b.crew ?? [], capacity = BUILDINGS[b.type].workerSlots ?? 0
    if (pending) {
      if (crew.includes(pending.unit) === pending.add) {
        notice = `${pending.name} ${pending.add ? 'joined the crew' : 'removed from the crew'}.`
        pending = null
      } else if (!st.connected || Date.now() - pending.at > 5000) {
        pending = null; notice = 'Crew update not confirmed. Check the connection and try again.'
      }
    }
    const full = crew.length >= capacity
    const automatic = !b.manualStaffing && st.self?.governor?.enabled !== false
    const message = !st.connected ? 'Reconnecting… Crew changes are unavailable.' : pending
      ? `${pending.add ? 'Assigning' : 'Removing'} ${pending.name}…` : notice || (full ? 'Crew full. Remove someone to open a place.' : 'Changes apply immediately. Choose who works here.')
    if (feedback.textContent !== message) feedback.textContent = message
    auto.disabled = !st.connected || !!pending || st.self?.governor?.enabled === false
    auto.classList.toggle('hidden', automatic)
    auto.title = st.self?.governor?.enabled === false ? 'Automatic management is off in the Village Council.' : ''
    const rows = rowsFor(b)
    const key = JSON.stringify([b.id, b.name, crew, b.workers, b.paused, automatic, filter, search.value, st.connected, pending?.unit, rows.map(r => [r.unit.id, r.unit.person!.name, r.unit.person!.held, Math.round(r.skill * 10), personTitle(r.unit.person!), r.activity, r.unavailable, r.available])])
    if (signature === key) return
    signature = key
    const scrollTop = scroll.scrollTop
    const focus = document.activeElement instanceof HTMLElement && panel.contains(document.activeElement) ? document.activeElement : null
    const focusId = focus?.dataset.crewAdd ?? focus?.dataset.crewRemove
    const focusAction = focus?.dataset.crewAdd !== undefined ? 'data-crew-add' : 'data-crew-remove'
    subtitle.textContent = `${b.name || BUILDINGS[b.type].name} · ${automatic ? 'Automatic staffing' : 'Manually chosen'}${b.paused ? ' · Paused' : ''}`
    count.textContent = `${crew.length} / ${capacity} places filled`
    count.classList.toggle('is-full', full)
    slots.replaceChildren()
    const color = colorOf(st, b.owner)
    for (let i = 0; i < capacity; i++) {
      const id = crew[i], unit = id === undefined ? undefined : st.units.get(id)
      const slot = el('div', `crew-slot${id === undefined ? ' crew-slot-empty' : ''}`)
      if (id === undefined) {
        const plus = el('span', 'crew-slot-symbol', '+'); plus.setAttribute('aria-hidden', 'true')
        const copy = el('div', 'crew-slot-copy')
        copy.append(el('strong', 'crew-slot-name', `Open place ${i + 1}`), el('span', 'crew-slot-note', 'Choose a villager below'))
        slot.append(plus, copy)
      } else {
        if (unit) slot.append(crewPortrait(unit, color))
        const name = unit?.person?.name ?? `Villager #${id}`
        const copy = el('div', 'crew-slot-copy')
        copy.append(el('strong', 'crew-slot-name', name))
        if (unit?.person) copy.append(el('span', 'crew-slot-note', `${SKILL_LABEL[jobSkill(b.type)]} ${(unit.person.skill[jobSkill(b.type)] ?? 0).toFixed(1)} · ${unit.person.held ? ITEMS[unit.person.held].name : 'Bare hands'}`))
        copy.append(el('span', 'crew-slot-note', unit ? unitActivity(st, unit) : 'Outside view'))
        const remove = el('button', 'crew-remove', '✕')
        remove.type = 'button'; remove.dataset.crewRemove = String(id)
        remove.setAttribute('aria-label', `Remove ${name} from crew`)
        remove.disabled = !st.connected || !!pending || !!unit?.hauler
        slot.append(copy, remove)
      }
      slots.append(slot)
    }
    for (const button of filters.querySelectorAll<HTMLButtonElement>('button')) {
      const active = filter === button.dataset.crewFilter
      button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active))
    }
    const rank = (r: Candidate): number => r.unavailable ? 3 : r.available ? 0 : r.post && r.post.id !== b.id ? 2 : 1
    const fit = (r: Candidate): number => b.type === 'farm' ? farmSkillMult(r.skill) * (r.equipped ? 2 : 1)
      : b.type === 'pier' ? (r.equipped ? 2 : 0) : skillMult(r.skill) * (r.equipped ? TOOL_GATHER_MULT : BARE_HAND_MULT)
    const pool = rows.filter(r => !crew.includes(r.unit.id))
    pool.sort((a, z) => (filter === 'skilled' ? Number(!!a.unavailable) - Number(!!z.unavailable) || z.skill - a.skill : rank(a) - rank(z) || fit(z) - fit(a) || z.skill - a.skill) || a.unit.person!.name.localeCompare(z.unit.person!.name) || a.unit.id - z.unit.id)
    const best = [...pool].filter(r => r.available && fit(r) > 0).sort((a, z) => fit(z) - fit(a) || z.skill - a.skill)[0]?.unit.id
    const query = search.value.trim().toLocaleLowerCase()
    const visible = pool.filter(r => (filter !== 'available' || r.available) && (!query || [r.unit.person!.name, r.activity, r.unavailable, personTitle(r.unit.person!), r.unit.person!.held ? ITEMS[r.unit.person!.held!].name : 'bare hands'].join(' ').toLocaleLowerCase().includes(query)))
    results.textContent = `${visible.length} ${visible.length === 1 ? 'villager' : 'villagers'}`
    listTitle.textContent = full ? 'Find someone to swap in' : 'Choose your people'
    candidates.replaceChildren()
    for (const row of visible) {
      const { unit, equipped, available, unavailable } = row, person = unit.person!
      const recommended = unit.id === best
      const card = el('article', `crew-card${recommended ? ' is-recommended' : ''}${!available ? ' is-busy' : ''}${unavailable ? ' is-unavailable' : ''}`)
      card.dataset.crewUnit = String(unit.id)
      const cardTop = el('div', 'crew-card-top')
      const copy = el('div', 'crew-person')
      copy.append(el('h4', 'crew-person-name', person.name), el('span', 'crew-person-title', personTitle(person) || 'Learning a trade'))
      const badge = el('span', 'crew-badge', unavailable ? 'Unavailable' : recommended ? 'Best available' : available ? 'Available' : 'Busy')
      cardTop.append(crewPortrait(unit, color), copy, badge)
      const skill = el('div', 'crew-skill')
      skill.append(el('span', 'crew-skill-label', SKILL_LABEL[jobSkill(b.type)]), el('strong', 'crew-skill-value', `${(Math.round(row.skill * 10) / 10).toFixed(1)} / 10`))
      const track = el('div', 'crew-skill-track'), fill = el('span', '')
      fill.style.width = `${Math.max(0, Math.min(100, row.skill * 10))}%`; track.append(fill); track.setAttribute('aria-hidden', 'true'); skill.append(track)
      const equipment = el('div', `crew-equipment${equipped ? '' : ' is-missing'}`)
      const toolIcon = el('span', 'crew-tool-icon')
      toolIcon.innerHTML = itemIconSvg(person.held ?? requiredTool(b.type), 24)
      equipment.append(toolIcon)
      equipment.append(el('span', '', equipped ? `${ITEMS[person.held!].name} equipped` : `${person.held ? ITEMS[person.held].name : 'Bare hands'} · ${ITEMS[requiredTool(b.type)].name.toLowerCase()} recommended`))
      const cardFoot = el('div', 'crew-card-foot')
      const note = unavailable || (b.type === 'farm' ? `${Math.round(farmSkillMult(row.skill) * 100)}% skill output${equipped ? ' · hoe ×2' : ''}` : row.post ? 'Reassigns their current post' : equipped ? 'Right tool for this job' : b.type === 'pier' || b.type === 'barracks' ? 'Needs the right tool to work' : 'Can learn on the job')
      const add = el('button', 'btn crew-hire', unavailable ? 'Unavailable' : full ? 'Crew full' : row.post ? 'Move here' : 'Assign')
      add.type = 'button'; add.dataset.crewAdd = String(unit.id)
      add.disabled = !st.connected || !!pending || full || !!unavailable
      add.setAttribute('aria-label', `${row.post ? 'Move' : 'Assign'} ${person.name} to ${b.name || BUILDINGS[b.type].name}`)
      cardFoot.append(el('span', 'crew-card-note', note), add)
      card.append(cardTop, skill, equipment, el('p', 'crew-activity', row.activity), cardFoot)
      candidates.append(card)
    }
    if (!visible.length) candidates.append(el('div', 'crew-empty', query ? 'No villagers match that search. Try another name or tool.' : filter === 'available' ? 'No idle villagers. All villagers shows current jobs and availability.' : 'No other villagers to choose from yet. New arrivals will appear here.'))
    if (focusId && !focus?.isConnected) {
      const target = panel.querySelector<HTMLButtonElement>(`[${focusAction}="${focusId}"]:not(:disabled)`) ?? panel.querySelector<HTMLButtonElement>(`[data-crew-remove="${focusId}"]:not(:disabled)`)
      ;(target ?? search).focus({ preventScroll: true })
    }
    scroll.scrollTop = scrollTop
  }

  function open(id: number): void {
    buildingId = id
    if (!current()) { buildingId = null; return }
    previousFocus = document.activeElement as HTMLElement | null
    filter = 'all'; search.value = ''; signature = ''; notice = ''; pending = null; gesture = false
    const b = current()!
    const preview = previewFor(b.type)
    art.replaceChildren()
    if (preview) { const img = el('img', ''); img.src = preview; img.alt = ''; art.append(img) }
    else art.textContent = BUILD_ICON[b.type]
    back.classList.remove('hidden'); refresh(); scroll.scrollTop = 0
    close.focus({ preventScroll: true })
  }
  st.on('ui:assign', id => { if (typeof id === 'number') open(id) })
  // Building and unit updates need to stay visible even between Self messages.
  setInterval(refresh, 250)
  return { open, refresh }
}
